import RootAdmin from "../../../components/layouts/RootAdmin";
import { Button, Card, CardBody, CardHeader, Chip, Divider, Input, Tab, Tabs, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Tooltip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@nextui-org/react";
import { EyeIcon, CloudArrowDownIcon, ChevronDownIcon, CheckIcon, XMarkIcon, ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { BASE_URL, getFetcher, patchFetcher, patchFetcherWithoutId, putFetcher } from "../../../services/api";
import ReactSelect from "react-select";
import { gayaSelectDalamTabel, sasaranPortalMenu } from "../../../services/gayaSelectDalamTabel";
import useSWR from "swr";
import useCustomNavigate from "../../../hooks/useCustomNavigate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formattedDate, hasValidRole } from "../../../services/helpers";
import useAuth from "../../../hooks/useAuth";
import RolesAccess from "../../../enums/roles";
import { differenceInDays } from "date-fns";
import StatusRekomendasi from "../../../enums/statusRekomendasi";
import { endOfYear, format, startOfYear } from "date-fns";
import ClientDataGrid from "../../../components/elements/ClientDataGrid";
import FormAddSK from "../../../components/fragments/admin/recomendation/FormAddSK";
import TimeProcessBadge from "../../../components/fragments/admin/recomendation/TimeProcessBadge";
import { toast } from "react-hot-toast";

import { calculateRegistrasiRekomendasiProcessingDaysWithTanggalPengajuan } from "../../../services/helpers";

export default function IndexPage() {
  const { roles } = useAuth();
  const isDirektur = hasValidRole(roles, [RolesAccess.DIREKTUR]);
  const canDelegasikan = hasValidRole(roles, [RolesAccess.KOORDINATOR, RolesAccess.SUPER_ADMIN]);
  // Koordinator (murni, tanpa role SuperAdmin/Direktur) hanya boleh mendelegasikan,
  // tidak perlu melihat/membuka detail data lewat kolom Aksi.
  const hideAksiForKoordinator = hasValidRole(roles, [RolesAccess.KOORDINATOR]) && !hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.DIREKTUR]);

  const { data: picRekomendasiData } = useSWR(canDelegasikan ? "/api/users/search-users?returnAll=true&roleName=PIC Rekomendasi" : null, getFetcher);
  const picRekomendasiOptions = useMemo(() => picRekomendasiData?.data?.map((item) => ({ value: item.id, label: item.fullName })) || [], [picRekomendasiData]);
  const [startDate, setStartDate] = useState(""); // for filters
  const [endDate, setEndDate] = useState("");
  const [keyword, setKeyword] = useState("");
  const [isLoadingSync, setIsLoadingSync] = useState(false);

  const { getRecomendationDetailPath } = useCustomNavigate();
  const [activeTab, setActiveTab] = useState("draft");

  // State untuk inline edit tanggal validasi
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // State untuk modal konfirmasi proses
  const [isOpenProcessModal, setIsOpenProcessModal] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
  const [selectedCancelId, setSelectedCancelId] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);

  const [isOpenSyncModal, setIsOpenSyncModal] = useState(false);
  const [syncStartDate, setSyncStartDate] = useState("");
  const [syncEndDate, setSyncEndDate] = useState("");

  const apiEndpoint = useMemo(() => {
    let baseUrl = "";
    const searchParam = keyword ? `&search=${encodeURIComponent(keyword)}` : "";
    const dateParams = startDate && endDate && activeTab === "riwayat" ? `&startDate=${startDate}&endDate=${endDate}` : "";

    switch (activeTab) {
      case "draft":
        if (isDirektur) {
          baseUrl = `/api/rekom/permohonan/search?status=${StatusRekomendasi.DRAFT_SK_TANDA_TANGAN_DIREKTUR},${StatusRekomendasi.MENGIRIM_SK_REKOMENDASI_B3_KE_SPIONAM}&returnAll=true&sortBy=tanggalPengajuan&sortOrder=asc`;
        } else {
          baseUrl = `/api/rekom/permohonan/search?returnAll=true&sortBy=tanggalPengajuan&sortOrder=desc`;
        }
        break;
      case "riwayat":
        baseUrl = `/api/rekom/permohonan/search?returnAll=true&status=${StatusRekomendasi.SELESAI}`;
        break;
      default:
        return null;
    }

    return `${baseUrl}${searchParam}${dateParams}`;
  }, [activeTab, startDate, endDate, keyword, isDirektur]);

  // Set default tanggal awal tahun dan akhir tahun
  useEffect(() => {
    const currentYearStart = format(startOfYear(new Date()), "yyyy-MM-dd");
    const currentYearEnd = format(endOfYear(new Date()), "yyyy-MM-dd");
    setStartDate(currentYearStart);
    setEndDate(currentYearEnd);
  }, []);

  const { data, isLoading, mutate } = useSWR(apiEndpoint, getFetcher);

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
  };

  // handles for the filter component
  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleSyncGoogleForm = async (syncType = "all") => {
    if (syncType === "date") {
      // Buka modal untuk memilih tanggal
      setIsOpenSyncModal(true);
      return;
    }

    setIsLoadingSync(true);
    try {
      const endpoint = "/api/datarekom/sync";
      const response = await getFetcher(endpoint);

      // Show success message
      toast.success(response.message);

      // Show warning for failed items
      if (response.failed && response.failed.length > 0) {
        response.failed.forEach((failedItem) => {
          toast.error(`${failedItem.row["Nama Perusahaan"] ?? "Row " + failedItem.rowIndex}: ${failedItem.error}`);
        });
      }

      // Refresh data
      mutate();
    } catch (error) {
      toast.error("Gagal melakukan sync data dari Google Form");
      console.error("Sync error:", error);
    } finally {
      setIsLoadingSync(false);
    }
  };

  // Function untuk sync berdasarkan range tanggal
  const handleSyncByDateRange = async () => {
    if (!syncStartDate || !syncEndDate) {
      toast.error("Harap pilih tanggal mulai dan tanggal akhir");
      return;
    }

    setIsLoadingSync(true);
    try {
      const endpoint = `/api/datarekom/sync?startDate=${syncStartDate}&endDate=${syncEndDate}`;
      const response = await getFetcher(endpoint);

      // Show success message
      toast.success(response.message);

      // Show warning for failed items
      if (response.failed && response.failed.length > 0) {
        response.failed.forEach((failedItem) => {
          toast.error(`${failedItem.row["Nama Perusahaan"] ?? "Row " + failedItem.rowIndex}: ${failedItem.error}`);
        });
      }

      // Refresh data
      mutate();

      // Close modal and reset dates
      setIsOpenSyncModal(false);
      setSyncStartDate("");
      setSyncEndDate("");
    } catch (error) {
      toast.error("Gagal melakukan sync data dari Google Form");
      console.error("Sync error:", error);
    } finally {
      setIsLoadingSync(false);
    }
  };

  // Function untuk menutup modal sync
  const closeSyncModal = () => {
    setIsOpenSyncModal(false);
    setSyncStartDate("");
    setSyncEndDate("");
  };

  const handleUpdateTanggalValidasi = async (id, newDate) => {
    setIsUpdating(true);
    try {
      console.log("Updating ID:", id, "with date:", newDate);
      await patchFetcherWithoutId(`/api/rekom/permohonan/set-validation-date`, {
        id: id,
        tanggalValidasi: newDate,
      });

      toast.success("Tanggal validasi berhasil diperbarui");
      mutate(); // Refresh data
      setEditingRowId(null);
      setEditingValue("");
    } catch (error) {
      const msg = error?.response?.data?.message?.error || error?.response?.data?.message || "Gagal memperbarui tanggal validasi";
      console.error("Update error:", error);
      toast.error(msg);
    } finally {
      setIsUpdating(false);
    }
  };

  // Function untuk memulai edit
  const startEdit = (rowId, currentValue) => {
    setEditingRowId(rowId);
    // Format tanggal untuk input date (YYYY-MM-DD)
    const formattedValue = currentValue ? format(new Date(currentValue), "yyyy-MM-dd") : "";
    setEditingValue(formattedValue);
  };

  // Function untuk cancel edit
  const cancelEdit = () => {
    setEditingRowId(null);
    setEditingValue("");
  };

  // Function untuk save edit
  const saveEdit = (rowId) => {
    if (editingValue) {
      handleUpdateTanggalValidasi(rowId, editingValue);
    } else {
      cancelEdit();
    }
  };

  // Function untuk handle proses permohonan
  const handleProcessPermohonan = async (id) => {
    setIsProcessing(true);
    try {
      console.log("Processing permohonan ID:", id);
      await patchFetcher(`/api/rekom/permohonan/process`, id, {});

      toast.success("Permohonan berhasil diproses");
      mutate(); // Refresh data
      closeProcessModal();
    } catch (error) {
      const msg = error?.response?.data?.message?.error || error?.response?.data?.message || "Gagal memproses permohonan";
      console.error("Process error:", error);
      toast.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function untuk handle cancel proses permohonan
  const handleCancelPermohonan = async (id) => {
    setIsCanceling(true);
    try {
      console.log("Canceling permohonan ID:", id);
      await patchFetcher(`/api/rekom/permohonan/cancel-process`, id, {});

      toast.success("Proses permohonan berhasil dibatalkan");
      mutate(); // Refresh data
      closeCancelModal();
    } catch (error) {
      const msg = error?.response?.data?.message?.error || error?.response?.data?.message || "Gagal membatalkan proses permohonan";
      console.error("Cancel error:", error);
      toast.error(msg);
    } finally {
      setIsCanceling(false);
    }
  };

  // Function untuk membuka modal konfirmasi cancel
  const openCancelModal = (id) => {
    setSelectedCancelId(id);
    setIsOpenCancelModal(true);
  };

  // Function untuk menutup modal konfirmasi cancel
  const closeCancelModal = () => {
    setSelectedCancelId(null);
    setIsOpenCancelModal(false);
  };

  // Function untuk konfirmasi cancel
  const confirmCancel = () => {
    if (selectedCancelId) {
      handleCancelPermohonan(selectedCancelId);
    }
  };

  // Function untuk membuka modal konfirmasi
  const openProcessModal = (id) => {
    setSelectedProcessId(id);
    setIsOpenProcessModal(true);
  };

  // Function untuk menutup modal konfirmasi
  const closeProcessModal = () => {
    setSelectedProcessId(null);
    setIsOpenProcessModal(false);
  };

  // Function untuk konfirmasi proses
  const confirmProcess = () => {
    if (selectedProcessId) {
      handleProcessPermohonan(selectedProcessId);
    }
  };

  const handleDelegasiChange = useCallback(
    async (id, delegasiUserId) => {
      try {
        await patchFetcherWithoutId(`/api/rekom/permohonan/${id}/delegasi`, { delegasiUserId });
        toast.success("Delegasi berhasil diperbarui");
        mutate();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Gagal memperbarui delegasi");
      }
    },
    [mutate]
  );

  const renderDelegasiCell = useCallback(
    (params) => {
      if (!canDelegasikan) {
        return params?.row?.delegasiUser?.fullName || "-";
      }
      const currentValue = params?.row?.delegasiUser ? { value: params.row.delegasiUser.id, label: params.row.delegasiUser.fullName } : null;
      return (
        <ReactSelect
          className="w-full"
          placeholder="Pilih PIC..."
          isClearable
          value={currentValue}
          options={picRekomendasiOptions}
          onChange={(option) => handleDelegasiChange(params.row.id, option?.value ?? null)}
          // Daftar PIC digambar di <body> supaya tidak terpotong tabel; lihat
          // services/gayaSelectDalamTabel.js untuk alasannya.
          menuPortalTarget={sasaranPortalMenu()}
          menuPlacement="auto"
          styles={gayaSelectDalamTabel}
        />
      );
    },
    [canDelegasikan, picRekomendasiOptions, handleDelegasiChange]
  );

  const columns = useMemo(
    () => [
      { field: "tanggalPengajuan", headerName: "Tanggal Pengajuan", valueGetter: (value, row) => formattedDate(row.tanggalPengajuan) },
      { field: "kodePermohonan", headerName: "Kode Registrasi" },
      { field: "company", headerName: "Perusahaan", valueGetter: (value, row) => row.company.name },
      { field: "jenisPermohonan", headerName: "Jenis Permohonan" },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => (
          <Chip size="sm" color={params?.row?.status === StatusRekomendasi.MENUNGGU_DI_PROSES ? "warning" : "primary"} variant="flat">
            {params?.row?.status}
          </Chip>
        ),
      },
      {
        field: "delegasi",
        headerName: "Delegasi",
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: renderDelegasiCell,
      },
      {
        field: "tanggalValidasi",
        headerName: "Tanggal Validasi",
        width: 200,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;

          if (isEditing) {
            return (
              <div className="flex items-center gap-1">
                <Input type="date" size="sm" variant="bordered" value={editingValue} onChange={(e) => setEditingValue(e.target.value)} className="w-32" isDisabled={isUpdating} />
                <Button isIconOnly size="sm" color="success" variant="light" onPress={() => saveEdit(params?.row?.id)} isLoading={isUpdating} isDisabled={isUpdating}>
                  <CheckIcon className="w-4 h-4" />
                </Button>
                <Button isIconOnly size="sm" color="danger" variant="light" onPress={cancelEdit} isDisabled={isUpdating}>
                  <XMarkIcon className="w-4 h-4" />
                </Button>
              </div>
            );
          }

          return (
            <div className={`cursor-pointer hover:bg-surface-3 p-1 rounded transition-colors ${params.row.tanggalValidasi ?? "underline text-blue-600"}`} onClick={() => startEdit(params.row.id, params.row.tanggalValidasi)}>
              {params.row.tanggalValidasi ? formattedDate(params.row.tanggalValidasi) : "Set tanggal"}
            </div>
          );
        },
        sortable: false,
      },
      {
        field: "lamaProses",
        headerName: "Lama Proses",
        renderCell: (params) => {
          let lamaProses = 0;

          // Jika tanggalValidasi dan tanggalSelesai sudah diset, hitung selisih keduanya
          if (params.row.tanggalValidasi && params.row.tanggalSelesai) {
            lamaProses = differenceInDays(new Date(params.row.tanggalSelesai), new Date(params.row.tanggalValidasi));
          }
          // Jika hanya tanggalValidasi yang diset, hitung selisih dengan hari ini
          else if (params.row.tanggalValidasi) {
            lamaProses = differenceInDays(new Date(), new Date(params.row.tanggalValidasi));
          }
          // Pastikan nilai tidak negatif
          lamaProses = Math.max(0, lamaProses);

          return (
            <div className="flex flex-col items-center">
              <Chip color={lamaProses <= 2 ? "success" : "danger"} variant="flat" size="sm">
                {lamaProses} Hari
              </Chip>
            </div>
          );
        },
      },

      {
        field: "action",
        headerName: "Aksi",
        renderCell: (params) => {
          const canCancel = params?.row?.status !== StatusRekomendasi.SELESAI && params?.row?.isProcessed;

          return (
            <div className="flex gap-2">
              <Button size="sm" color="primary" isIconOnly onClick={() => getRecomendationDetailPath(params?.row?.id)}>
                <EyeIcon className="size-4" />
              </Button>
              {!params?.row?.isProcessed && (
                <Tooltip content="Proses">
                  <Button size="sm" color="warning" isIconOnly onClick={() => openProcessModal(params?.row?.id)}>
                    <ArrowPathIcon className="size-4" />
                  </Button>
                </Tooltip>
              )}
              {canCancel && (
                <Tooltip content="Batalkan Proses">
                  <Button size="sm" color="danger" isIconOnly onClick={() => openCancelModal(params?.row?.id)}>
                    <XMarkIcon className="size-4" />
                  </Button>
                </Tooltip>
              )}
            </div>
          );
        },
        sortable: false,
        filterable: false,
      },
    ].filter((col) => !(hideAksiForKoordinator && col.headerName === "Aksi")),
    [getRecomendationDetailPath, renderDelegasiCell, hideAksiForKoordinator]
  );

  const columnsRiwayat = useMemo(
    () => [
      { field: "kodePermohonan", headerName: "Kode Permohonan" },
      { field: "tanggalPengajuan", headerName: "Tanggal Pengajuan", valueGetter: (value, row) => formattedDate(row.tanggalPengajuan) ?? "-" },
      { field: "nomorSurat", headerName: "Nomor Surat", valueGetter: (value, row) => row.draftSurat?.nomorSuratFormatted },
      { field: "company", headerName: "Perusahaan", valueGetter: (value, row) => row.company.name },
      { field: "tipeSurat", headerName: "Tipe Permohonan" },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => (
          <Chip size="sm" color="primary" variant="flat">
            {params?.row?.status}
          </Chip>
        ),
      },
      {
        field: "delegasi",
        headerName: "Delegasi",
        valueGetter: (value, row) => row?.delegasiUser?.fullName || "-",
      },
      {
        field: "lamaProses",
        headerName: "Lama Proses",
        renderCell: (params) => {
          let lamaProses = 0;

          // Jika tanggalValidasi dan tanggalSelesai sudah diset, hitung selisih keduanya
          if (params.row.tanggalValidasi && params.row.tanggalSelesai) {
            lamaProses = differenceInDays(new Date(params.row.tanggalSelesai), new Date(params.row.tanggalValidasi));
          }
          // Jika hanya tanggalValidasi yang diset, hitung selisih dengan hari ini
          else if (params.row.tanggalValidasi) {
            lamaProses = differenceInDays(new Date(), new Date(params.row.tanggalValidasi));
          }
          // Pastikan nilai tidak negatif
          lamaProses = Math.max(0, lamaProses);

          return (
            <div className="flex flex-col items-center">
              <Chip color={lamaProses <= 2 ? "success" : "danger"} variant="flat" size="sm">
                {lamaProses} Hari
              </Chip>
            </div>
          );
        },
      },
      { field: "nomorSuratPerusahaan", headerName: "Nomor Surat Perusahaan" },
      { field: "tanggalSuratPerusahaan", headerName: "Tanggal Surat Perusahaan", valueGetter: (value, row) => formattedDate(row.tanggalSuratPerusahaan) ?? "-" },
      { field: "nomorSuratRekomendasi", headerName: "Nomor Surat Rekomendasi" },
      { field: "tanggalSuratRekomendasi", headerName: "Tanggal Surat Rekomendasi", valueGetter: (value, row) => formattedDate(row.tanggalSuratRekomendasi) ?? "-" },
      { field: "tanggalSurat", headerName: "Tanggal Surat", valueGetter: (value, row) => formattedDate(row.draftSurat?.tanggalSurat) ?? "-" },
      { field: "tanggalSelesai", headerName: "Tanggal Selesai", valueGetter: (value, row) => formattedDate(row.tanggalSelesai) ?? "-" },
      { field: "tanggalBerlaku", headerName: "Tanggal Berlaku", valueGetter: (value, row) => formattedDate(row.tanggalBerlaku) ?? "-" },
      { field: "tanggalBerakhir", headerName: "Tanggal Berakhir", valueGetter: (value, row) => formattedDate(row.tanggalBerakhir) ?? "-" },
      { field: "identitasPemohon", headerName: "Identitas Pemohon", valueGetter: (value, row) => row.identitasPemohon?.namaPemohon ?? "-" },
      { field: "jumlahKendaraan", headerName: "Jumlah Kendaraan", valueGetter: (value, row) => row.vehicles?.length },
      { field: "bahanB3", headerName: "Jumlah Bahan B3", valueGetter: (value, row) => row.b3Substances?.length },
      {
        field: "file_surat",
        headerName: "File SK",
        renderCell: (params) => {
          if (!params?.row?.file_surat) {
            return <FormAddSK id={params?.row?.id} mutate={mutate} />;
          } else {
            const buktiUrl = `${BASE_URL}/${params?.row?.file_surat}`;
            return (
              <a href={buktiUrl} target="_blank" rel="noopener noreferrer" className="text-primary-500 underline">
                Lihat File SK
              </a>
            );
          }
        },
      },
      {
        field: "action",
        headerName: "Aksi",
        renderCell: (params) => {
          // Hanya render tombol pada baris pertama
          return (
            <div className="flex gap-2">
              <Button size="sm" color="primary" isIconOnly onClick={() => getRecomendationDetailPath(params?.row?.id)}>
                <EyeIcon className="size-4" />
              </Button>
            </div>
          );
        },
        sortable: false,
        filterable: false,
      },
    ].filter((col) => !(hideAksiForKoordinator && col.headerName === "Aksi")),
    [hideAksiForKoordinator]
  );

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader className="flex justify-between items-center">
          <span>Daftar Permohonan Rekomendasi</span>
          {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REKOMENDASI]) && (
            <Dropdown>
              <DropdownTrigger>
                <Button size="sm" color="primary" startContent={<CloudArrowDownIcon className="size-4" />} endContent={<ChevronDownIcon className="size-3" />} isLoading={isLoadingSync} isDisabled={isLoadingSync}>
                  {isLoadingSync ? "Mengambil Data..." : "Ambil Data dari Google Form"}
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Sync options" onAction={(key) => handleSyncGoogleForm(key)}>
                <DropdownItem key="date" className="text-primary">
                  Per Tanggal
                </DropdownItem>
                <DropdownItem key="all" className="text-primary">
                  Semua
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </CardHeader>
        <Divider />
        <CardBody className="w-full">
          <Tabs
            color="primary"
            variant="underlined"
            aria-label="tabs"
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key)}
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
              cursor: "w-full",
              tab: "max-w-fit px-1.5 h-12",
              tabContent: "group-data-[selected=true]:font-semibold",
            }}
          >
            <Tab title="Draft" key="draft">
              <div className="mb-4">
                <Input
                  type="text"
                  placeholder="Cari kode registrasi atau nama perusahaan..."
                  value={keyword}
                  onChange={handleKeywordChange}
                  startContent={<MagnifyingGlassIcon className="size-4 text-ink-400" />}
                  variant="bordered"
                  size="sm"
                  className="max-w-sm"
                  isClearable
                  onClear={() => setKeyword("")}
                />
              </div>
              <div className="h-[530px]">
                <ClientDataGrid rows={data?.applications || []} isLoading={isLoading} columns={columns} />
              </div>
            </Tab>
            <Tab title="Riwayat" key="riwayat">
              <Filter startDate={startDate} handleStartDate={handleStartDateChange} endDate={endDate} handleEndDate={handleEndDateChange} keyword={keyword} />
              <div className="h-[530px]">
                <ClientDataGrid
                  rows={data?.applications}
                  isLoading={isLoading}
                  columns={columnsRiwayat}
                  initialState={{
                    columns: {
                      columnVisibilityModel: {
                        tanggalSurat: false,
                        nomorSuratPerusahaan: false,
                        tanggalSuratPerusahaan: false,
                        nomorSuratRekomendasi: false,
                        tanggalSuratRekomendasi: false,
                        tanggalSelesai: false,
                        tanggalBerlaku: false,
                        tanggalBerakhir: false,
                        identitasPemohon: false,
                        jumlahKendaraan: false,
                        bahanB3: false,
                      },
                    },
                  }}
                />
              </div>
            </Tab>
          </Tabs>
        </CardBody>
      </Card>

      <Modal isOpen={isOpenProcessModal} onOpenChange={setIsOpenProcessModal} onClose={closeProcessModal} size="md" backdrop="blur">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Konfirmasi Proses</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-2">
                  <p className="text-ink-700">Apakah Anda yakin ingin memproses permohonan ini?</p>
                  <p className="text-sm text-ink-500">Aksi ini akan menyimpan dan mengubah status permohonan.</p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose} isDisabled={isProcessing}>
                  Batal
                </Button>
                <Button color="warning" onPress={confirmProcess} isLoading={isProcessing} isDisabled={isProcessing} startContent={!isProcessing && <ArrowPathIcon className="size-4" />}>
                  {isProcessing ? "Memproses..." : "Ya, Proses"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Modal Konfirmasi Cancel */}
      <Modal isOpen={isOpenCancelModal} onOpenChange={setIsOpenCancelModal} onClose={closeCancelModal} size="md" backdrop="blur">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Konfirmasi Batalkan Proses</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-2">
                  <p className="text-ink-700">Apakah Anda yakin ingin membatalkan proses permohonan ini?</p>
                  <p className="text-sm text-ink-500">Aksi ini akan mengembalikan status permohonan ke kondisi sebelumnya.</p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="default" variant="light" onPress={onClose} isDisabled={isCanceling}>
                  Batal
                </Button>
                <Button color="danger" onPress={confirmCancel} isLoading={isCanceling} isDisabled={isCanceling} startContent={!isCanceling && <XMarkIcon className="size-4" />}>
                  {isCanceling ? "Membatalkan..." : "Ya, Batalkan"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal isOpen={isOpenSyncModal} onOpenChange={setIsOpenSyncModal} onClose={closeSyncModal} size="md" backdrop="blur">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Ambil Data Per Tanggal</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <p className="text-ink-700">Pilih rentang tanggal untuk ambil data dari Google Form.</p>

                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Tanggal Mulai</label>
                    <Input type="date" value={syncStartDate} onChange={(e) => setSyncStartDate(e.target.value)} variant="bordered" size="sm" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Tanggal Akhir</label>
                    <Input
                      type="date"
                      value={syncEndDate}
                      onChange={(e) => setSyncEndDate(e.target.value)}
                      variant="bordered"
                      size="sm"
                      min={syncStartDate} // Tanggal akhir tidak boleh kurang dari tanggal mulai
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="default" variant="light" onPress={onClose} isDisabled={isLoadingSync}>
                  Batal
                </Button>
                <Button color="primary" onPress={handleSyncByDateRange} isLoading={isLoadingSync} isDisabled={isLoadingSync || !syncStartDate || !syncEndDate} startContent={!isLoadingSync && <CloudArrowDownIcon className="size-4" />}>
                  {isLoadingSync ? "Mengambil Data..." : "Sync Data"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}

function Filter({ startDate, handleStartDate, endDate, handleEndDate }) {
  return (
    <div className="w-full flex justify-end gap-4 mb-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs">Tanggal Mulai</label>
        <Input type="date" variant="underlined" onChange={handleStartDate} value={startDate} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs">Tanggal Akhir</label>
        <Input type="date" variant="underlined" onChange={handleEndDate} value={endDate} />
      </div>
    </div>
  );
}
